import * as React from "react";

import { useControlled } from "../forms/useControlled";
import { mergeProps } from "../utils/mergeProps";
import "./collapsible.css";

/**
 * Collapsible, CollapsibleTrigger and CollapsiblePanel, replacing `@salt-ds/core`'s.
 *
 * ── THE TRIGGER AND THE PANEL FIND EACH OTHER THROUGH A CONTEXT ───────────────────────
 *
 * A disclosure is only accessible if the control SAYS what it controls: the trigger carries
 * `aria-expanded` and `aria-controls`, and `aria-controls` has to name the panel's id. The
 * panel mints that id and publishes it upward — the same shape as `FormField` and `Table` in
 * this design system, and for the same reason. Without it the trigger announces "collapsed"
 * with no indication of what is collapsed.
 *
 * ── HIDDEN TWICE, DELIBERATELY ────────────────────────────────────────────────────────
 *
 * A closed panel gets BOTH `hidden` and `aria-hidden="true"`. That looks redundant and is not:
 * CSS can override `hidden` (`[hidden] { display: block }` in a reset, or any rule with higher
 * specificity), and a panel that is visually hidden by a height animation is still in the
 * accessibility tree. Belt and braces is correct here — content a sighted user cannot see must
 * not be readable by a screen reader either.
 *
 * ── onOpenChange TAKES THE EVENT FIRST ────────────────────────────────────────────────
 *
 * `(event, open)`, not `(open)`. That is the vendor's signature and call sites depend on the
 * event — a disclosure inside a row often needs to stop the click reaching the row underneath.
 */
interface CollapsibleContextValue {
  open: boolean;
  setOpen: (event: React.SyntheticEvent, open: boolean) => void;
  panelId?: string;
  setPanelId?: (id: string) => void;
}

const CollapsibleContext = React.createContext<CollapsibleContextValue>({
  open: false,
  setOpen: () => undefined,
});

export function useCollapsibleContext(): CollapsibleContextValue {
  return React.useContext(CollapsibleContext);
}

export interface CollapsibleProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (event: React.SyntheticEvent, open: boolean) => void;
}

export const Collapsible = React.forwardRef<HTMLDivElement, CollapsibleProps>(function Collapsible(
  { className, defaultOpen, onOpenChange, open: openProp, ...rest },
  ref,
) {
  const [open, setOpenState] = useControlled<boolean>({
    controlled: openProp,
    default: Boolean(defaultOpen),
  });
  const [panelId, setPanelId] = React.useState<string | undefined>(undefined);

  const setOpen = (event: React.SyntheticEvent, next: boolean) => {
    setOpenState(next);
    onOpenChange?.(event, next);
  };

  // Not memoised: `setOpen` closes over the caller's `onOpenChange`, which closes over page
  // state. Pinning that froze a handler earlier in this migration and silently dropped writes.
  const contextValue: CollapsibleContextValue = { open, setOpen, panelId, setPanelId };

  return (
    <CollapsibleContext.Provider value={contextValue}>
      <div
        className={className ? `saltCollapsible ${className}` : "saltCollapsible"}
        ref={ref}
        {...rest}
      />
    </CollapsibleContext.Provider>
  );
});

export interface CollapsibleTriggerProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
}

export const CollapsibleTrigger = React.forwardRef<HTMLElement, CollapsibleTriggerProps>(
  function CollapsibleTrigger({ children, className, onClick, ...rest }, ref) {
    const { open, panelId, setOpen } = useCollapsibleContext();

    if (!children || !React.isValidElement(children)) return <>{children}</>;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setOpen(event, !open);
      onClick?.(event);
    };

    /*
     * The trigger CLONES the caller's element — usually their own button — rather than wrapping
     * it. A wrapper would add a box to the layout and, worse, would put the aria attributes on a
     * div while the real control sat inside it announcing nothing.
     *
     * MERGED, not spread. A plain spread lets the child's `onClick` REPLACE the toggle and its
     * className replace `saltCollapsibleTrigger`. See `../utils/mergeProps` — the onClick half
     * of that had already shipped in OverlayTrigger before this component's own tests caught the
     * className half.
     */
    return React.cloneElement(children, {
      ...mergeProps(
        {
          className: className
            ? `saltCollapsibleTrigger ${className}`
            : "saltCollapsibleTrigger",
          "aria-expanded": open,
          "aria-controls": panelId,
          onClick: handleClick,
          ...rest,
        },
        children.props as Record<string, unknown>,
      ),
      ref,
    } as React.HTMLAttributes<HTMLElement>);
  },
);

export type CollapsiblePanelProps = React.HTMLAttributes<HTMLDivElement>;

export const CollapsiblePanel = React.forwardRef<HTMLDivElement, CollapsiblePanelProps>(
  function CollapsiblePanel({ children, className, id: idProp, ...rest }, ref) {
    const generatedId = React.useId();
    const id = idProp ?? generatedId;
    const { open, setPanelId } = useCollapsibleContext();

    React.useEffect(() => {
      // Published upward so the TRIGGER can point `aria-controls` at this panel.
      if (id) setPanelId?.(id);
    }, [id, setPanelId]);

    return (
      <div
        aria-hidden={!open ? "true" : undefined}
        className={className ? `saltCollapsiblePanel ${className}` : "saltCollapsiblePanel"}
        hidden={!open}
        id={id}
        ref={ref}
        {...rest}
      >
        {/* The inner element is what the height animation measures; the outer one is clipped. */}
        <div className="saltCollapsiblePanel-inner">{children}</div>
      </div>
    );
  },
);
